// MEDIA-02 Free path (ARCHITECTURE §10.2): the browser decodes the photo, re-encodes it (which strips
// EXIF, including GPS), and makes a master (longest edge ≤ 2048 px) plus the fixed widths 320 / 640 /
// 1080 / 1600 that fit, in WebP (JPEG when the browser can't encode WebP). Raw originals never leave
// the device. Then: tickets → PUT each object → complete (the server verifies every one).
import { catalogApi, putObject, type MediaAsset, type UploadRequest } from "../products/api";

export const ACCEPTED = ["image/jpeg", "image/png", "image/webp", "image/avif"];
export const MAX_SOURCE_BYTES = 15 * 1024 * 1024;
const MAX_EDGE = 2048;
const WIDTHS = [320, 640, 1080, 1600] as const;

export class UploadRejected extends Error {
  constructor(readonly reason: "type" | "size") {
    super(reason);
  }
}

interface Encoded {
  blob: Blob;
  width: number;
  height: number;
  format: "webp" | "jpeg";
}

async function encode(bitmap: ImageBitmap, width: number, height: number): Promise<Encoded> {
  const canvas = document.createElement("canvas");
  canvas.width = width;
  canvas.height = height;
  const ctx = canvas.getContext("2d");
  if (!ctx) throw new Error("canvas unavailable");
  ctx.imageSmoothingQuality = "high";
  ctx.drawImage(bitmap, 0, 0, width, height);
  const toBlob = (type: string, q: number) =>
    new Promise<Blob | null>((res) => canvas.toBlob((b) => res(b), type, q));
  const webp = await toBlob("image/webp", 0.82);
  if (webp && webp.type === "image/webp") return { blob: webp, width, height, format: "webp" };
  const jpeg = await toBlob("image/jpeg", 0.85);
  if (!jpeg) throw new Error("encode failed");
  return { blob: jpeg, width, height, format: "jpeg" };
}

async function sha256Hex(blob: Blob): Promise<string> {
  const d = await crypto.subtle.digest("SHA-256", await blob.arrayBuffer());
  return [...new Uint8Array(d)].map((b) => b.toString(16).padStart(2, "0")).join("");
}

/** One photo → a ready asset. */
export async function uploadPhoto(
  orgId: string,
  storeId: string,
  file: File,
  idempotencyKey: string,
): Promise<MediaAsset> {
  if (!ACCEPTED.includes(file.type)) throw new UploadRejected("type");
  if (file.size > MAX_SOURCE_BYTES) throw new UploadRejected("size");
  const bitmap = await createImageBitmap(file);
  const scale = Math.min(1, MAX_EDGE / Math.max(bitmap.width, bitmap.height));
  const mw = Math.max(1, Math.round(bitmap.width * scale));
  const mh = Math.max(1, Math.round(bitmap.height * scale));
  const master = await encode(bitmap, mw, mh);
  const renditions: Encoded[] = [];
  for (const w of WIDTHS) {
    if (w > mw) break;
    renditions.push(await encode(bitmap, w, Math.max(1, Math.round((mh * w) / mw))));
  }
  bitmap.close();
  const body: UploadRequest = {
    purpose: "product",
    filename: file.name.slice(0, 200) || "photo",
    content_type: master.blob.type,
    size: master.blob.size,
    sha256: await sha256Hex(master.blob),
    width: mw,
    height: mh,
    store_id: storeId,
    renditions: renditions.map((r) => ({
      variant: `w${r.width}` as "w320",
      format: r.format,
      size: r.blob.size,
      width: r.width,
    })),
  };
  const tickets = await catalogApi.createUpload(orgId, body, idempotencyKey);
  const blobs = new Map<string, Blob>([
    ["master", master.blob],
    ...renditions.map((r) => [`w${r.width}`, r.blob] as const),
  ]);
  // The master and its renditions are independent objects: upload them together, not one by one.
  await Promise.all(
    tickets.uploads.map((u) => {
      const blob = blobs.get(u.variant);
      return blob ? putObject(u.url, blob) : undefined;
    }),
  );
  return catalogApi.completeUpload(orgId, tickets.asset_id);
}

export function formatBytes(n: number): { key: "b" | "kb" | "mb" | "gb"; n: string } {
  if (n < 1024) return { key: "b", n: String(n) };
  if (n < 1024 ** 2) return { key: "kb", n: (n / 1024).toFixed(0) };
  if (n < 1024 ** 3) return { key: "mb", n: (n / 1024 ** 2).toFixed(1) };
  return { key: "gb", n: (n / 1024 ** 3).toFixed(1) };
}
